import {useRef, useEffect} from 'react';

import Box from '@mui/material/Box';
import PropTypes from 'prop-types';

const DEFAULT_THRESHOLD = 50;
const DEFAULT_SCROLL_SPEED = 10;

const AutoScroll = ({
  children,
  threshold = DEFAULT_THRESHOLD, // pixels from edge to trigger scroll
  scrollSpeed = DEFAULT_SCROLL_SPEED,
  enabled = true, // allows disabling the auto-scroll
  horizontal = true, // enable horizontal scroll
  vertical = true, // enable vertical scroll
  className = ''
}) => {
  const containerRef = useRef(null);
  const scrollIntervalRef = useRef(null);

  // eslint-disable-next-line complexity
  const handleScroll = e => {
    if (!enabled) return;

    const container = containerRef.current;
    if (!container) return;

    const rect = container.getBoundingClientRect();
    const mouseX = e.clientX;
    const mouseY = e.clientY;

    // Distances from edges
    const distanceFromBottom = rect.bottom - mouseY;
    const distanceFromTop = mouseY - rect.top;
    const distanceFromRight = rect.right - mouseX;
    const distanceFromLeft = mouseX - rect.left;

    // Clear any existing interval
    if (scrollIntervalRef.current) {
      clearInterval(scrollIntervalRef.current);
      scrollIntervalRef.current = null;
    }

    // Decide scroll direction
    if (vertical && distanceFromBottom < threshold) {
      scrollIntervalRef.current = setInterval(() => {
        container.scrollTop += scrollSpeed;
      }, 16);
    } else if (vertical && distanceFromTop < threshold) {
      scrollIntervalRef.current = setInterval(() => {
        container.scrollTop -= scrollSpeed;
      }, 16);
    } else if (horizontal && distanceFromRight < threshold) {
      scrollIntervalRef.current = setInterval(() => {
        container.scrollLeft += scrollSpeed;
      }, 16);
    } else if (horizontal && distanceFromLeft < threshold) {
      scrollIntervalRef.current = setInterval(() => {
        container.scrollLeft -= scrollSpeed;
      }, 16);
    }
  };

  const handleMouseLeave = () => {
    if (scrollIntervalRef.current) {
      clearInterval(scrollIntervalRef.current);
    }
  };

  useEffect(() => {
    return () => {
      if (scrollIntervalRef.current) {
        clearInterval(scrollIntervalRef.current);
      }
    };
  }, []);

  return (
    <Box
      role="region"
      ref={containerRef}
      className={className}
      onMouseMove={handleScroll}
      onMouseLeave={handleMouseLeave}
      sx={{
        height: '100%',
        maxHeight: '80vh',
        overflow: 'auto',
        whiteSpace: horizontal ? 'nowrap' : 'normal'
      }}
    >
      {children}
    </Box>
  );
};

AutoScroll.propTypes = {
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.arrayOf(PropTypes.node)]).isRequired,
  threshold: PropTypes.number,
  scrollSpeed: PropTypes.number,
  enabled: PropTypes.bool,
  horizontal: PropTypes.bool,
  vertical: PropTypes.bool,
  className: PropTypes.string
};

export default AutoScroll;
